import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

/**
 * The page shell: header, content, footer, filling the viewport.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS, AND WHAT IT FIXES
 *
 * Every page previously wrote its own `<div className="min-h-dvh">` wrapper. That makes
 * the WRAPPER at least a viewport tall, but its children still stack from the top — so on
 * a short page the footer sits wherever the content happens to end, with the rest of the
 * viewport empty BELOW it. It reads as an unfinished page, and it was most obvious on the
 * payment-confirmed screen, which is deliberately brief.
 *
 * The fix is the ordinary one: make the shell a flex column and let `main` absorb the
 * slack. The footer then sits after the content on a long page and at the bottom of the
 * viewport on a short one, WITHOUT being fixed or sticky — nothing overlays the content,
 * and scrolling behaves exactly as before.
 *
 * It lives here because `src/layout/README.md` already says the page shell belongs in
 * this directory. Pages describe their content; the shell owns this geometry, so no page
 * has to remember it and no page can get it subtly different.
 */
export default function PageShell({
  children,
  /**
   * How wide the content column is. Pages differ — a legal document is wider than a
   * sign-in form — so the measurement stays with the page while the geometry stays here.
   */
  width = 'mx-auto w-full max-w-2xl px-5 py-10 sm:px-8 sm:py-14',
  /**
   * Whether to render the footer. A couple of screens deliberately omit it; passing the
   * choice keeps that explicit rather than having them hand-roll a different shell.
   */
  footer = true,
}: {
  children: React.ReactNode;
  width?: string;
  footer?: boolean;
}) {
  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />
      {/* `flex-1` is the whole fix: main takes the leftover height, so the footer is
          pushed to the bottom on short pages and simply follows the content on long
          ones. `min-w-0` keeps a wide child (a table, a long URL) from forcing the
          column wider than the viewport on mobile. */}
      <main className={`${width} flex-1 min-w-0`}>{children}</main>
      {footer && <SiteFooter />}
    </div>
  );
}
